import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { commonContent } from './content.constant';
import { UtilsService } from '@core/util-services/utils.service';
import { appConfig } from 'app/constants/url-mapping.constant';
import { catchError, forkJoin, from, map, of } from 'rxjs';


@Injectable({
    providedIn: 'root'
})

/**
 * Service responsible for managing and providing content text for the application.
 * It supports loading content from both local files and remote S3 URLs, depending on the institution configuration.
 */
export class ContentService {

    /**
     * Constructs the ContentService.
     * @param utilService - Utility service used for deep merging of content objects.
     */
    constructor(
        private utilService: UtilsService
    ) {}

    /**
     * Static property to hold the loaded content.
     */
    static content: any = {};

    /**
     * Institution ID fetched from the environment configuration.
     */
    private institutionId = environment.institutionId;

    /**
     * URL for fetching common content configuration from S3.
     */
    private commonContentS3Url = `${environment.s3ConfigUrl}cs-config/text-config/V2/common_text.json`;

    /**
     * URL for fetching institution-specific content configuration from S3.
     */
    private institutionContentS3Url = `${environment.s3ConfigUrl}cs-config/text-config/V2/${this.institutionId.toUpperCase()}_text.json`;

    /**
     * Retrieves a specific content text value based on a dot-separated key.
     * @param key - Dot-separated key to locate the desired content text.
     * @returns The content text corresponding to the provided key.
     */
    getContentText(key: string): any {
        return key.split('.').reduce(function (cur, objKey) {
            return cur != null ? cur[objKey] : null;
        }, ContentService.content);
    }

    /**
     * Initializes the content service by loading content based on the institution configuration.
     * For rollout institutions, content is loaded from S3. For non-rollout institutions, content is loaded locally.
     * @returns A promise that resolves when the content is successfully loaded.
     */
    init(): Promise<void> {
        if (!appConfig.nonRolloutInstitutions.includes(this.institutionId)) {
            return this.loadFromS3(); // Load from S3 for rollout institutions
        } else {
            return new Promise<void>((resolve, reject) => {
                import(`./content-${this.institutionId.toLowerCase()}.constant`)
                    .then((data: any) => {
                        ContentService.content = this.utilService.mergeDeep(commonContent, data.content);
                        resolve();
                    })
                    .catch(() => {
                        ContentService.content = commonContent;
                        resolve();
                    });
            });
        }
    }

    /**
     * Loads content from S3 for rollout institutions.
     * Combines common content and institution-specific content into a single object.
     * @returns A promise that resolves when the content is successfully loaded or rejects on failure.
     */
    private loadFromS3(): Promise<void> {
        return forkJoin({
            commonConfig: from(this.fetchData(this.commonContentS3Url)),
            institutionConfig: from(this.fetchData(this.institutionContentS3Url))
        })
        .pipe(
            catchError(error => {
            console.error('Failed to load institution-specific content, falling back to common only.', error);
            return from(this.fetchData(this.commonContentS3Url)).pipe(
                catchError(finalError => {
                console.error('Failed to load even the common content:', finalError);
                throw finalError;
                }),
                map(commonConfig => ({ commonConfig, institutionConfig: {} }))
            );
            })
        )
        .toPromise()
        .then(result => {
            const { commonConfig, institutionConfig } = result;
            ContentService.content = this.utilService.mergeDeep(commonConfig, institutionConfig);
        })
        .catch(err => {
            console.error('Error processing content:', err);
            throw err;
        });
    }

    /**
     * Fetches JSON data from a given URL.
     * @param url - The URL to fetch data from.
     * @returns A promise that resolves with the parsed JSON data.
     */
    async fetchData(url: string): Promise<any> {
        const response = await fetch(url);
        const data = await response.json();
        return data.deposit;
    }
}
